import { getPortalRoot } from '../portalRoot';
import { providerOrder, videoOptions, type Provider } from './providers';
const scripts = new Map<string, Promise<void>>();
export function loadAdScript(src: string, placement: 'head' | 'body' = 'body'): Promise<void> {
  const existing = scripts.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new Error('Ad script timed out'));
    }, 12000);
    script.src = src;
    script.async = true;
    script.charset = 'UTF-8';
    script.onload = () => { clearTimeout(timer); resolve(); };
    script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Ad script unavailable')); };
    document[placement].appendChild(script);
  });
  scripts.set(src, promise);
  void promise.catch(() => scripts.delete(src));
  return promise;
}
declare global {
  interface Window {
    addContentRoll?: (options: ReturnType<typeof videoOptions>) => void;
    UTInventoryCore?: new (options: Record<string, unknown>) => { destroy?: () => void };
  }
}
export function ensureAdBoundary() {
  getPortalRoot();
  if (document.getElementById('last_element')) return;
  const marker = document.createElement('div');
  marker.id = 'last_element';
  marker.hidden = true;
  document.body.appendChild(marker);
}

/** Same cleanup as santa-frontend: remove every sibling after the marker. */
export function cleanupAll() {
  const marker = document.getElementById('last_element');
  if (!marker) return;
  let node = marker.nextSibling;
  while (node) {
    const next = node.nextSibling;
    if (node instanceof HTMLScriptElement) scripts.delete(node.src);
    node.remove();
    node = next;
  }
}
export function hasVideoContent(provider: Provider) {
  const host = document.querySelector(provider.selector)
    ?? (provider.name === 'moe_video' ? document.getElementById('moevideo') : null);
  const root = host?.shadowRoot ?? host;
  if (!root) return false;
  // Provider wrappers can exist while empty. Ignore tiny tracking frames.
  return [...root.querySelectorAll('iframe, video')].some((node) => {
    const bounds = node.getBoundingClientRect();
    const style = getComputedStyle(node);
    return bounds.width > 40 && bounds.height > 40
      && style.display !== 'none' && Number(style.opacity) > .01;
  });
}

/** One weighted fallback chain per document, with legacy post-load wait times. */
export function createVideoLoader({ order, load, initialize, filled, retire, wait = (ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)), now = Date.now }: {
  order: () => Provider[];
  load: (src: string) => Promise<void>;
  initialize: (provider: Provider) => void;
  filled: (provider: Provider) => boolean;
  retire: (provider: Provider) => void;
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
}) {
  let run: Promise<void> | undefined;
  return (signal: AbortSignal): Promise<void> => {
    if (signal.aborted) return Promise.resolve();
    // Subsequent mounts reuse the existing chain instead of starting competitors.
    run ??= (async () => {
      for (const provider of order()) {
        if (signal.aborted) return;
        try {
          await Promise.all(provider.scripts.map((src) => load(src)));
          if (signal.aborted) { retire(provider); return; }
          initialize(provider);
          if (provider.name === 'nemedia') {
            // Legacy behavior: give Nemedia the full 12 seconds before checking.
            await wait(provider.timeout);
            if (!signal.aborted && filled(provider)) return;
          } else {
            const deadline = now() + provider.timeout;
            while (!signal.aborted && now() < deadline) {
              if (filled(provider)) return;
              await wait(1000);
            }
          }
        } catch {
          // Script failures still fall through instead of hanging indefinitely.
        }
        retire(provider);
      }
    })();
    return run;
  };
}
export const loadVideoAds = createVideoLoader({
  order: () => {
    ensureAdBoundary();
    cleanupAll();
    return providerOrder();
  },
  load: loadAdScript,
  filled: hasVideoContent,
  retire: cleanupAll,
  initialize: (provider) => {
    if (provider.name === 'moe_video') {
      window.addContentRoll?.(videoOptions(matchMedia('(max-width: 48rem)').matches));
    }
  },
});

/** Page styles cannot cross Moe.video's open shadow root. */
export function syncVideoShadowStyles() {
  const root = document.getElementById('moevideo')?.shadowRoot;
  if (!root || root.getElementById('santa-video-offset')) return;
  const style = document.createElement('style');
  style.id = 'santa-video-offset';
  style.textContent = '#mv-slot { bottom: var(--santa-ad-bottom, 0px) !important; }';
  root.appendChild(style);
}
